import React, {useCallback, useMemo} from 'react';
import {getFormSubmitErrors} from 'redux-form';
import {useAppSelector} from '../../../../../redux';
import {Platform, StyleSheet, View} from 'react-native';
import {BaseField} from '../../../../../shared/components/baseField';
import {Field, InjectedFormProps} from 'redux-form';
import {IStateProps} from './model';
import {NamedStyles, StyleHelper, useStyles} from '../../../../../shared/hooks/styles';
import {Button} from '../../../../../shared/components/button';
import {RequestForm} from '../../../../../shared/models/supportService';
import {stylePatterns} from '../../../../../shared/theme/patterns';
import {DeviceSize} from '../../../../../shared/theme/helpers';
import {FilePicker} from '../../../../../shared/components/filePicker';
import {Text} from '../../../../../shared/components/text';
import {CustomScrollView} from '../../../../../shared/components/scrollView';
import {useIsDesktop} from '../../../../../shared/hooks/useIsDesktop';
import {Form} from '../../../../../shared/components/form';
import {DictionaryItem} from '../../../../../shared/dictionaries/model';
import {Forms} from '../../../../../shared/models/forms';

import {FileItem} from './fileItem';

export default ({
  isLoading,
  handleSubmit,
  onSubmit,
  onFilePickerUploadEnd,
  onPickedFileRemove,
  pickedFiles,
  onFilePickerUploadStart,
  onFilePickerUploadError,
  uploadFilesActive,
  feedbackThemes,
  limits,
}: IStateProps & InjectedFormProps<RequestForm, IStateProps>) => {
  const {maxFileSizeMB, authorizedFileFormats, messageMinLength, messageMaxLength, maxFilesCount} = limits;

  const formErrors: RequestForm = useAppSelector((state) =>
    getFormSubmitErrors(Forms.createRequest)(state),
  ) as RequestForm;

  const styles = useStyles((helper) => createStyle(helper));
  const submit = useCallback(handleSubmit(onSubmit), [handleSubmit, onSubmit]);
  const fieldInlineStyles = {width: '100%', flex: 1};
  const isDesktop = useIsDesktop();
  const feedbackThemesAsDictionaryValues = useMemo<DictionaryItem[]>(
    () =>
      feedbackThemes.map((v) => ({
        id: v.id || 0,
        value: v.name || '',
      })),
    [feedbackThemes],
  );

  return (
    <Form style={[styles.wrap]} onSubmit={submit}>
      <View testID='feedback-theme' style={[styles.fieldWrap, formErrors.topic ? styles.selectFieldWrapError : null]}>
        <Field
          variant='outlined'
          type='select'
          name='topic'
          component={BaseField}
          props={{
            values: feedbackThemesAsDictionaryValues,
            iosPlaceholderItem: {id: -1, value: '', label: 'Тема обращения'},
          }}
          label='Тема обращения'
          style={fieldInlineStyles}
          onSubmitEditing={submit}
        />
      </View>
      <View testID='feedback-message' style={[styles.fieldWrap, formErrors.text ? styles.textFieldWrapError : null]}>
        <Field
          multiline
          rows={7}
          rowsMax={isDesktop ? Infinity : 7}
          type='text'
          name='text'
          label='Сообщение'
          maxLength={messageMaxLength}
          minLength={messageMinLength}
          variant='outlined'
          component={BaseField}
          style={fieldInlineStyles}
        />
      </View>
      <View style={styles.filePicker}>
        {pickedFiles.length > 0 ? (
          <CustomScrollView style={styles.files} contentContainerStyle={styles.filesInnerContainer}>
            {pickedFiles.map((item, index) => (
              <FileItem
                key={index}
                fileName={item.file.name}
                onCloseClick={() => onPickedFileRemove(index)}
                isLoading={isLoading}
                fileNumber={index}
              />
            ))}
          </CustomScrollView>
        ) : null}
        <View style={styles.filePickerPick}>
          {pickedFiles.length < maxFilesCount ? (
            <FilePicker
              pickerOptions={{multiple: true}}
              onUploadError={onFilePickerUploadError}
              onUploadEnd={onFilePickerUploadEnd}
              onUploadStart={onFilePickerUploadStart}
              disabled={isLoading || uploadFilesActive}
            />
          ) : null}
          <Text testID='available-format-text-id' style={styles.filePickerHint}>
            Загрузка файлов разрешена только в следующих форматах: {authorizedFileFormats.map((ext) => ext).join(', ')}
            {'\n'}
            Общий размер файлов не должен превышать {maxFileSizeMB}МБ
            {'\n'}
            Общее количество файлов не должно превышать {maxFilesCount}
          </Text>
        </View>
      </View>
      <View style={styles.controls}>
        <Button
          id='create-request-send'
          style={styles.submitButton}
          text='Отправить'
          disabled={isLoading || uploadFilesActive}
          onPress={submit}
        />
      </View>
    </Form>
  );
};

const fl = StyleSheet.flatten;

const createStyle = ({theme, mediaOnly, isDesktop}: StyleHelper): NamedStyles<any> => ({
  wrap: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
  },
  fieldWrap: fl([{marginBottom: 25}, mediaOnly(DeviceSize.desktop, {display: 'flex', flexDirection: 'row'})]),
  selectFieldWrapError: {
    ...mediaOnly(DeviceSize.mobile, {
      height: Platform.OS === 'web' ? ('inherit' as any) : 64,
    }),
    marginBottom: Platform.OS === 'web' && (DeviceSize.desktop || DeviceSize.mobile || DeviceSize.tablet) ? 1 : 25,
  },
  textFieldWrapError: {
    marginBottom: Platform.OS === 'web' && (DeviceSize.desktop || DeviceSize.mobile || DeviceSize.tablet) ? 1 : 25,
  },
  controls: fl([
    stylePatterns.flexCenterHorizontal,
    mediaOnly(
      DeviceSize.desktop,
      {},
      {
        paddingBottom: 117,
      },
    ),
  ]),
  filePicker: {
    marginBottom: isDesktop ? 50 : 60,
  },
  filePickerPick: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  filePickerHint: {
    marginTop: 15,
    color: theme.supportServiceCreateTopicForm.filePickerHintColor,
    paddingTop: isDesktop ? 2 : 5,
    ...mediaOnly(DeviceSize.desktop, theme.fonts.body, theme.fonts.label),
  },
  files: fl([
    {marginBottom: 10},
    mediaOnly(DeviceSize.desktop, {
      maxHeight: 250,
    }),
  ]),
  filesInnerContainer: {display: 'flex', flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between'},
  submitButton: mediaOnly(DeviceSize.desktop, {width: 200}, {flex: 1, margin: 0}),
});
